/**
 * Capital-gain tax rates by taxpayer type and sale year (Income Tax Ordinance, sections 91, 121, 126).
 *
 * Individuals:
 * - real gain accrued until 31.12.2002 ("המועד הקובע"): marginal rates of section 121 (editable, top rate by default);
 * - real gain accrued 1.1.2003 – 31.12.2011: up to 20% (section 91(ב), before amendment 187);
 * - real gain accrued from 1.1.2012 ("מועד השינוי"): up to 25% (section 91(ב)).
 * Companies: the section 126(א) corporate rate of the sale year on every period (section 91(א)).
 * The taxable inflationary amount is taxed at 10% (section 91(ג)).
 */

export const REAL_GAIN_PERIOD_KEYS = ['realUntil2002', 'real2003To2011', 'realFrom2012'];

export const CAPITAL_GAIN_RATE_KEYS = [
    'inflationaryTaxableRate',
    'realUntil2002Rate',
    'real2003To2011Rate',
    'realFrom2012Rate',
];

export const INFLATIONARY_TAXABLE_RATE = 10;

/** Section 126(א) corporate tax rate; each entry applies from that year onward. */
export const CORPORATE_TAX_RATE_BY_YEAR = {
    2006: 31,
    2007: 29,
    2008: 27,
    2009: 26,
    2010: 25,
    2011: 24,
    2012: 25,
    2014: 26.5,
    2016: 25,
    2017: 24,
    2018: 23,
};

/** Section 121 top marginal rate including the section 121ב surtax. */
export const INDIVIDUAL_MARGINAL_CAP = 50;
export const INDIVIDUAL_DEFAULT_MARGINAL_RATE = 47;
export const INDIVIDUAL_REAL_2003_TO_2011_RATE = 20;
export const INDIVIDUAL_REAL_FROM_2012_RATE = 25;

const COMPANY_CASE_TYPES = new Set(['חברה', 'בע"מ', 'בע״מ']);

function toYear(value) {
    if (value == null || value === '') return null;
    const year = Number(value);
    return Number.isNaN(year) ? null : year;
}

/**
 * @param {string|null|undefined} caseType
 * @returns {'company'|'individual'}
 */
export function getCapitalGainTaxpayerType(caseType) {
    const normalized = (caseType || '').toString().trim();
    if (COMPANY_CASE_TYPES.has(normalized)) return 'company';
    return 'individual';
}

export function getCorporateTaxRateForYear(saleYear) {
    const year = toYear(saleYear) ?? new Date().getFullYear();
    const years = Object.keys(CORPORATE_TAX_RATE_BY_YEAR)
        .map(Number)
        .sort((a, b) => a - b);
    let rate = CORPORATE_TAX_RATE_BY_YEAR[years[0]];
    years.forEach((y) => {
        if (y <= year) rate = CORPORATE_TAX_RATE_BY_YEAR[y];
    });
    return rate;
}

/**
 * Default rate per column for the sale year and taxpayer type.
 * @returns {{ inflationaryTaxableRate: number, realUntil2002Rate: number, real2003To2011Rate: number, realFrom2012Rate: number }}
 */
export function getDefaultCapitalGainTaxRates(saleYear, caseType) {
    if (getCapitalGainTaxpayerType(caseType) === 'company') {
        const corporateRate = getCorporateTaxRateForYear(saleYear);
        return {
            inflationaryTaxableRate: INFLATIONARY_TAXABLE_RATE,
            realUntil2002Rate: corporateRate,
            real2003To2011Rate: corporateRate,
            realFrom2012Rate: corporateRate,
        };
    }
    return {
        inflationaryTaxableRate: INFLATIONARY_TAXABLE_RATE,
        realUntil2002Rate: INDIVIDUAL_DEFAULT_MARGINAL_RATE,
        real2003To2011Rate: INDIVIDUAL_REAL_2003_TO_2011_RATE,
        realFrom2012Rate: INDIVIDUAL_REAL_FROM_2012_RATE,
    };
}

/** Upper bound for each editable rate column. */
export function getCapitalGainTaxRateCaps(saleYear, caseType) {
    if (getCapitalGainTaxpayerType(caseType) === 'company') {
        const corporateRate = getCorporateTaxRateForYear(saleYear);
        return {
            inflationaryTaxableRate: INFLATIONARY_TAXABLE_RATE,
            realUntil2002Rate: corporateRate,
            real2003To2011Rate: corporateRate,
            realFrom2012Rate: corporateRate,
        };
    }
    return {
        inflationaryTaxableRate: INFLATIONARY_TAXABLE_RATE,
        realUntil2002Rate: INDIVIDUAL_MARGINAL_CAP,
        real2003To2011Rate: INDIVIDUAL_REAL_2003_TO_2011_RATE,
        realFrom2012Rate: INDIVIDUAL_REAL_FROM_2012_RATE,
    };
}

/**
 * Section and ceiling shown under the tax table.
 * @returns {{ rate: number, section: string, taxpayerType: 'company'|'individual' }}
 */
export function getMaxCapitalGainTaxConfig(saleYear, caseType) {
    const taxpayerType = getCapitalGainTaxpayerType(caseType);
    if (taxpayerType === 'company') {
        return { rate: getCorporateTaxRateForYear(saleYear), section: '91(א)', taxpayerType };
    }
    const year = toYear(saleYear);
    const rate =
        year != null && year < 2012
            ? INDIVIDUAL_REAL_2003_TO_2011_RATE
            : INDIVIDUAL_REAL_FROM_2012_RATE;
    return { rate, section: '91(ב)', taxpayerType };
}

/** Clamp editable tax rate inputs to [0, maxRate]. */
export function normalizeTaxRate(value, maxRate) {
    const num = Number(value || 0);
    if (Number.isNaN(num)) return 0;
    return Math.min(Math.max(num, 0), maxRate);
}

/** Merge user rates over the defaults and clamp each to its cap. */
export function resolveCapitalGainTaxRates(rates, saleYear, caseType) {
    const defaults = getDefaultCapitalGainTaxRates(saleYear, caseType);
    const caps = getCapitalGainTaxRateCaps(saleYear, caseType);
    return CAPITAL_GAIN_RATE_KEYS.reduce((acc, key) => {
        const raw = rates?.[key];
        acc[key] =
            raw == null || raw === ''
                ? defaults[key]
                : normalizeTaxRate(raw, caps[key]);
        return acc;
    }, {});
}
